import { Button, EmptyState } from '@uroute/ui';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, CloudOff, Package } from '../components/icons';
import { JobCard } from '../components/JobCard';
import { JobsSkeleton } from '../components/Skeleton';
import { PullToRefresh } from '../components/PullToRefresh';
import { useMyJobs } from '../lib/jobs';
import { formatDayHeading } from '../lib/format';
import { formatUpdated } from '../lib/offline';
import { usePrefs, setPrefs } from '../lib/prefs';
import { useInstall } from '../lib/install';

export function TodayPage() {
  const navigate = useNavigate();
  const view = useMyJobs();
  const { onDuty } = usePrefs();
  const { canInstall, installed, dismissed, promptInstall } = useInstall();

  const heading = formatDayHeading(new Date());
  const active = view.active.length;
  const today = view.today.length;

  return (
    <PullToRefresh onRefresh={() => view.refresh()}>
      <section className="hero" data-testid="today-hero">
        <div className="hero__top">
          <div>
            <p className="hero__date u-data">{heading}</p>
            <h1 className="hero__title">Today&rsquo;s jobs</h1>
          </div>
          <button
            type="button"
            className={`dutychip dutychip--${onDuty ? 'on' : 'off'}`}
            onClick={() => setPrefs({ onDuty: !onDuty })}
            aria-pressed={onDuty}
            data-testid="duty-toggle"
          >
            <span className="dutychip__dot" aria-hidden="true" />
            {onDuty ? 'On duty' : 'Off duty'}
          </button>
        </div>
        <div className="hero__stats">
          <span className="hero__stat">
            <strong className="u-data" data-testid="today-count">{today}</strong>
            <span>due today</span>
          </span>
          <span className="hero__stat">
            <strong className="u-data" data-testid="active-count">{active}</strong>
            <span>active</span>
          </span>
          <span className="hero__stat">
            <strong className="u-data" data-testid="done-count">{view.completedToday.length}</strong>
            <span>done today</span>
          </span>
        </div>
        {view.offline ? (
          <p className="hero__offline">
            <CloudOff size={14} />
            <span>Offline — showing saved data{view.updatedAt ? ` (${formatUpdated(view.updatedAt)})` : ''}</span>
          </p>
        ) : null}
        {canInstall && !installed && !dismissed ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void promptInstall()}
            data-testid="install-button"
            className="hero__install"
          >
            Install app
          </Button>
        ) : null}
      </section>

      {view.error && !view.offline ? (
        <p className="form-error" role="alert" data-testid="jobs-error">
          {view.error}
        </p>
      ) : null}

      {view.loading ? (
        <JobsSkeleton count={3} />
      ) : today === 0 && active === 0 ? (
        <EmptyState
          icon={<CalendarCheck size={40} />}
          title={view.error ? 'Jobs unavailable' : 'No jobs today'}
          description={
            view.error
              ? 'Pull to refresh, or check your connection.'
              : view.offline
                ? 'New assignments will appear here once you reconnect.'
                : 'You&rsquo;re all clear — new assignments show up here.'
          }
          action={
            <Button variant="secondary" onClick={() => void view.refresh()} data-testid="refresh-jobs">
              Refresh
            </Button>
          }
        />
      ) : (
        <>
          {today > 0 ? (
            <>
              <h2 className="section-label" data-testid="section-today">Due today</h2>
              <ul className="joblist">
                {view.today.map((job) => (
                  <li key={job.id}>
                    <JobCard job={job} onClick={() => navigate(`/jobs/${job.id}`)} />
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {view.upcoming.length > 0 ? (
            <>
              <h2 className="section-label" data-testid="section-upcoming">Upcoming</h2>
              <ul className="joblist">
                {view.upcoming.map((job) => (
                  <li key={job.id}>
                    <JobCard job={job} onClick={() => navigate(`/jobs/${job.id}`)} />
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {view.completedToday.length > 0 ? (
            <>
              <h2 className="section-label" data-testid="section-completed">Completed today</h2>
              <ul className="joblist joblist--muted">
                {view.completedToday.map((job) => (
                  <li key={job.id}>
                    <JobCard job={job} onClick={() => navigate(`/jobs/${job.id}`)} />
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {view.completedEarlier.length > 0 ? (
            <>
              <h2 className="section-label" data-testid="section-earlier">Earlier</h2>
              <ul className="joblist joblist--muted">
                {view.completedEarlier.map((job) => (
                  <li key={job.id}>
                    <JobCard job={job} onClick={() => navigate(`/jobs/${job.id}`)} />
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {active === 0 && view.completedToday.length > 0 ? (
            <p className="hero__alldone">
              <Package size={16} />
              <span>All of today&rsquo;s deliveries are done — nice work.</span>
            </p>
          ) : null}

          <p className="list-updated u-data" data-testid="jobs-updated">
            Updated {formatUpdated(view.updatedAt)}
            {view.refreshing ? ' · refreshing…' : ''}
          </p>
        </>
      )}
    </PullToRefresh>
  );
}
